import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import { useTrackProductLikeAnalytics } from '@hooks/product/use-track-product-like-analytics';
import { useAuthentication } from '@context/authentication.context';
import useUserLikedProductsQuery from '@hooks/user/use-user-liked-products-query';
import { UseMutateFunction } from '@tanstack/react-query';
import { Product } from '@interfaces/models/product';
import { PageType } from '@interfaces/common/common';
import styles from './product-like-button.module.scss';

interface ProductLikeButtonProps {
  product: Product;
  customClass?: string;
  testIdSuffix?: string;
  position?: number;
  parentPosition?: string;
  pageType?: PageType;
  moduleType?: string;
  // TODO: add proper typing to this and useTrackProductLikeAnalytics
  alertTracking?: object;
  productFeedCategory?: string;
  showLikeCount?: boolean;
}

const ProductLikeButton = (props: ProductLikeButtonProps) => {
  const {
    product,
    customClass = '',
    testIdSuffix = '',
    position,
    parentPosition,
    pageType,
    moduleType,
    alertTracking,
    productFeedCategory,
    showLikeCount = false,
  } = props;

  const { t: tA11y } = useTranslation('a11y');
  const { setRedirectUrl } = useAuthentication();
  const {
    likedProducts = [],
    likeProduct,
    unlikeProduct,
  } = useUserLikedProductsQuery() as {
    likedProducts: string[];
    likeProduct: UseMutateFunction<void, Error, string, void>;
    unlikeProduct: UseMutateFunction<void, Error, string, void>;
  };
  const isProductLiked: boolean = likedProducts.includes(product.id);
  const productLikeCount: number = (product.likeCount ?? 0) + (likedProducts.includes(product.id) ? 1 : 0);

  const handleLike = (): void => {
    if (isProductLiked) {
      unlikeProduct(product.id);
    } else {
      likeProduct(product.id);
    }
  };

  const { trackOnLikeProduct, trackOnUnlikeProduct } = useTrackProductLikeAnalytics({
    product,
    position,
    parentPosition,
    pageType,
    moduleType,
    alertTracking,
    productFeedCategory,
  });

  const getAriaLabel = (): string => {
    if (isProductLiked) {
      return showLikeCount
        ? tA11y('A11Y.ICON.REMOVE_FAVORITES', { likeCount: productLikeCount })
        : tA11y('A11Y.PRODUCT_TILE.REMOVE_FAVORITES');
    }

    return showLikeCount
      ? tA11y('A11Y.ICON.ADD_FAVORITES', { likeCount: productLikeCount })
      : tA11y('A11Y.PRODUCT_TILE.ADD_FAVORITES');
  };

  const onClick = (event?: React.MouseEvent | React.KeyboardEvent): void => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isProductLiked) {
      trackOnUnlikeProduct();
    } else {
      trackOnLikeProduct();
    }
    handleLike();
  };

  return (
    <AuthenticationGuard
      onAuth={onClick}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        setRedirectUrl(null);
        event.stopPropagation();
      }}
      shouldPreventDefault={true}
    >
      <button
        type="button"
        aria-pressed={isProductLiked}
        aria-label={getAriaLabel()}
        className={clsx(customClass, styles.likeButton)}
        data-cy={`favorite_button${testIdSuffix}`}
        data-vc-dd-action-name={`like_button`}
        onClick={onClick}
      >
        {showLikeCount && <span data-cy={`product-like-button-like-count-${product.id}`}>{productLikeCount}</span>}
        {/* 'Like' icon */}
        {isProductLiked ? (
          <span
            className={clsx(styles.likeButton__icon, styles['likeButton__icon--liked'])}
            data-cy={`favorite_button__liked${testIdSuffix}`}
          />
        ) : (
          <span
            className={styles.likeButton__icon}
            data-cy={`favorite_button__like__icon${testIdSuffix}`}
          />
        )}
      </button>
    </AuthenticationGuard>
  );
};

export default ProductLikeButton;
